import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import {
  getTableActionsColumnWidth,
  TABLE_ACTIONS_COLUMN_MIN_WIDTH_PX,
} from '../../config/appConfig';
import { buildAdvancedTableVariant } from '../../config/tableUiTheme';
import {
  getActiveTableUiThemeId,
  subscribeTableUiTheme,
} from '../../config/tableUiThemeRuntime';
import {
  getTableActionsConfig,
  getTableActionsVisibilityClass,
} from '../../config/tableActionButtons';
import { DragHandleIcon, ArrowUpIcon, XIcon } from '../ui/Icons';
import { CELL_RENDERERS, renderDefault } from './tableCellRenderers';

// Table density configurations
const DENSITY_CONFIG = {
  compact: {
    name: 'Compact',
    rowHeight: 'h-8',
    padding: 'px-2 py-1',
    fontSize: 'text-xs'
  },
  normal: {
    name: 'Normal', 
    rowHeight: 'h-10',
    padding: 'px-3 py-2',
    fontSize: 'text-sm'
  },
  comfortable: {
    name: 'Comfortable',
    rowHeight: 'h-12',
    padding: 'px-4 py-3',
    fontSize: 'text-sm'
  }
};

// Custom sort orders for specific field types
const CUSTOM_SORT_ORDERS = {
  status: ['Active', 'Under Repair', 'Inactive', 'Sold'],
  vesselType: ['Container Ship', 'Bulk Carrier', 'Tanker', 'General Cargo', 'Ro-Ro', 'Cruise Ship'],
  priority: ['High', 'Medium', 'Low'],
  size: ['XL', 'L', 'M', 'S', 'XS']
};

const MIN_COLUMN_WIDTH_PX = 80;
const MAX_COLUMN_WIDTH_PX = 480;

const clampColumnWidthPx = (value, fallbackPx = 150) => {
  const px = parseInt(String(value), 10) || fallbackPx;
  return Math.min(MAX_COLUMN_WIDTH_PX, Math.max(MIN_COLUMN_WIDTH_PX, px));
};

const clampColumnWidthCss = (value, fallbackPx = 150) =>
  `${clampColumnWidthPx(value, fallbackPx)}px`;

/** Keep saved order for known keys; append any new config columns at the end. */
const mergeSavedColumnOrder = (savedOrder, currentColumns) => {
  const currentKeys = (currentColumns || []).map((col) => col.key);
  if (!currentKeys.length) return [];
  if (!Array.isArray(savedOrder) || savedOrder.length === 0) return currentKeys;

  const merged = savedOrder.filter((key) => currentKeys.includes(key));
  currentKeys.forEach((key) => {
    if (!merged.includes(key)) merged.push(key);
  });
  return merged;
};



const AdvancedTable = ({ 
  columns = [], 
  data = [], 
  density = 'normal',
  onDensityChange,
  tableId = 'default-table',
  className,
  maxHeight = '600px',
  customSortOrders = {},
  externalColumnWidths,
  externalColumnOrder,
  externalSortConfig,
  onConfigChange,
  variant = 'minimal',
  onFileCountClick,
  onTodoTaskCountClick,
  // Optional legacy prop; ignored for now to keep table clean
  moduleConfig
}) => {
  const { theme, isDark } = useTheme();
  const [tableUiThemeId, setTableUiThemeId] = useState(getActiveTableUiThemeId);

  useEffect(() => subscribeTableUiTheme(setTableUiThemeId), []);

  const tableVariant = useMemo(
    () => buildAdvancedTableVariant(tableUiThemeId, theme),
    [tableUiThemeId, theme]
  );
  const isRefinedTable = tableVariant.useZebraFromCss === true;

  // Get table actions configuration
  const actionsVisibility = getTableActionsConfig('visibility');
  const spacing = getTableActionsConfig('spacing');
  const [columnWidths, setColumnWidths] = useState({});
  const [columnOrder, setColumnOrder] = useState(columns.map(col => col.key));
  const [sortConfig, setSortConfig] = useState([]); // Array of {key, direction, priority}
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const tableRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const lastExternalSortRef = useRef(null);

  const actionsColumnWidth = useMemo(
    () => (moduleConfig ? getTableActionsColumnWidth(moduleConfig) : null),
    [moduleConfig]
  );

  const withoutActionsWidth = useCallback(
    (widths) => {
      if (!widths || !actionsColumnWidth) return widths || {};
      const next = { ...widths };
      delete next.actions;
      return next;
    },
    [actionsColumnWidth]
  );

  const widthForColumn = useCallback(
    (column) => {
      if (column.key === 'actions') {
        const raw = actionsColumnWidth || column.width || column.minWidth || '120px';
        const px = clampColumnWidthPx(raw, TABLE_ACTIONS_COLUMN_MIN_WIDTH_PX);
        return `${Math.max(px, TABLE_ACTIONS_COLUMN_MIN_WIDTH_PX)}px`;
      }
      const stored = columnWidths[column.key];
      return clampColumnWidthCss(stored || column.width || '150px');
    },
    [actionsColumnWidth, columnWidths]
  );

  const baselineColumnOrder = useMemo(
    () => columns.map((col) => col.key),
    [columns],
  );
  const columnKeysSignature = baselineColumnOrder.join('|');

  // Load saved column configuration from localStorage.
  // If the config's default column order changed, ignore saved order (keep widths/sort).
  useEffect(() => {
    const baselineOrder = columns.map((col) => col.key);
    const baselineSig = baselineOrder.join('|');
    const savedConfig = localStorage.getItem(`table-config-${tableId}`);
    if (savedConfig) {
      try {
        const {
          widths,
          order,
          sortConfig: savedSortConfig,
          baselineSig: savedBaselineSig,
        } = JSON.parse(savedConfig);
        if (widths) {
          const clamped = {};
          Object.entries(widths).forEach(([key, w]) => {
            clamped[key] = clampColumnWidthCss(w);
          });
          setColumnWidths(withoutActionsWidth(clamped));
        }
        if (
          Array.isArray(order) &&
          savedBaselineSig === baselineSig
        ) {
          setColumnOrder(mergeSavedColumnOrder(order, columns));
        } else {
          setColumnOrder(baselineOrder);
        }
        if (savedSortConfig) setSortConfig(savedSortConfig);
      } catch (error) {
        console.warn('Failed to load table configuration:', error);
        setColumnOrder(baselineOrder);
      }
    } else {
      setColumnOrder(baselineOrder);
    }
    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when tableId or default column order changes
  }, [tableId, columnKeysSignature]);

  // Save column configuration to localStorage
  const saveConfiguration = useCallback(() => {
    const config = {
      widths: withoutActionsWidth(columnWidths),
      order: columnOrder,
      sortConfig: sortConfig,
      // Used to detect when module default column order changes
      baselineSig: columnKeysSignature,
    };
    localStorage.setItem(`table-config-${tableId}`, JSON.stringify(config));
  }, [columnWidths, columnOrder, sortConfig, tableId, withoutActionsWidth, columnKeysSignature]);

  // Handle external configuration updates (only after initialization and not during resize)
  useEffect(() => {
    if (isInitialized && !isResizing && externalColumnWidths && JSON.stringify(externalColumnWidths) !== JSON.stringify(columnWidths)) {

      setColumnWidths(withoutActionsWidth(externalColumnWidths));
    }
  }, [isInitialized, isResizing, externalColumnWidths, columnWidths, withoutActionsWidth]);

  useEffect(() => {
    if (
      isInitialized &&
      !isDragging &&
      externalColumnOrder &&
      externalColumnOrder.length > 0 &&
      JSON.stringify(externalColumnOrder) !== JSON.stringify(columnOrder)
    ) {
      // Only accept parent order when it matches the current config column set
      const baseline = columns.map((col) => col.key);
      const sameKeys =
        externalColumnOrder.length === baseline.length &&
        baseline.every((key) => externalColumnOrder.includes(key));
      if (sameKeys) {
        setColumnOrder(externalColumnOrder);
      }
    }
  }, [isInitialized, isDragging, externalColumnOrder, columnOrder, columns]);

  // Sync parent → table only when external sort changes (not when local sortConfig changes)
  useEffect(() => {
    if (!isInitialized) return;
    const next = externalSortConfig ?? [];
    const serialized = JSON.stringify(next);
    if (lastExternalSortRef.current === serialized) return;
    lastExternalSortRef.current = serialized;
    setSortConfig(next);
  }, [isInitialized, externalSortConfig]);

  // Notify parent of configuration changes (debounced, only after initialization and not during resize/drag)
  useEffect(() => {
    if (isInitialized && !isResizing && !isDragging && onConfigChange) {
      const timer = setTimeout(() => {

        onConfigChange({
          columnWidths: withoutActionsWidth(columnWidths),
          columnOrder,
          sortConfig
        });
      }, 200); // Increased debounce to prevent rapid updates
      
      return () => clearTimeout(timer);
    }
  }, [isInitialized, isResizing, isDragging, columnWidths, columnOrder, sortConfig, onConfigChange, withoutActionsWidth]);

  // Save configuration when it changes
  useEffect(() => {
    const timer = setTimeout(saveConfiguration, 500);
    return () => clearTimeout(timer);
  }, [saveConfiguration]);

  // Handle column resizing
  const handleMouseDown = (e, columnKey) => {

    
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(columnKey);
    
    const startX = e.pageX;
    const currentWidth = columnWidths[columnKey];
    const startWidth = currentWidth ? (typeof currentWidth === 'string' ? parseInt(currentWidth) : currentWidth) : 150;
    


    const handleMouseMove = (e) => {
      const diff = e.pageX - startX;
      const newWidth = clampColumnWidthPx(startWidth + diff, startWidth);

      setColumnWidths(prev => {
        const updated = {
          ...prev,
          [columnKey]: `${newWidth}px`,
        };

        return updated;
      });
    };

    const handleMouseUp = () => {

      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // Notify parent of final configuration after resize ends
      setTimeout(() => {
        if (onConfigChange) {
          setColumnWidths(currentWidths => {
            const finalConfig = {
              columnWidths: currentWidths,
              columnOrder,
              sortConfig
            };

            onConfigChange(finalConfig);
            return currentWidths;
          });
        }
      }, 300);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Handle drag and drop reordering
  const handleDragStart = (e, columnKey) => {
    // Prevent drag if clicking on resize handle
    if (e.target.classList.contains('resize-handle') || e.target.closest('.resize-handle')) {
      e.preventDefault();
      return false;
    }
    

    setIsDragging(true);
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnKey);
    
    // Create a custom drag image
    const dragImage = e.currentTarget.cloneNode(true);
    dragImage.style.opacity = '0.8';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedColumn && draggedColumn !== columnKey) {

      setDragOverColumn(columnKey);
      e.dataTransfer.dropEffect = 'move';
    } else {
      e.dataTransfer.dropEffect = 'none';
    }
  };

  const handleDrop = (e, targetColumnKey) => {
    e.preventDefault();
    e.stopPropagation();
    

    
    if (draggedColumn && draggedColumn !== targetColumnKey) {
      const newOrder = [...columnOrder];
      const draggedIndex = newOrder.indexOf(draggedColumn);
      const targetIndex = newOrder.indexOf(targetColumnKey);
      

      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        // Remove dragged item and insert at target position
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, draggedColumn);
        
        setColumnOrder(newOrder);
        
        // Notify parent of final configuration after drop
        setTimeout(() => {
          if (onConfigChange) {
            const finalConfig = {
              columnWidths,
              columnOrder: newOrder,
              sortConfig
            };

            onConfigChange(finalConfig);
          }
        }, 300);
      }
    }
    
    setIsDragging(false);
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleDragEnd = () => {

    setIsDragging(false);
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  // Handle column sorting
  const handleSort = (columnKey, event) => {
    const isMultiSort = event.ctrlKey || event.metaKey; // Ctrl/Cmd for multi-column sort
    
    setSortConfig(prev => {
      if (!isMultiSort) {
        // Single column sort
        const existingSort = prev.find(s => s.key === columnKey);
        if (existingSort) {
          if (existingSort.direction === 'asc') {
            return [{ key: columnKey, direction: 'desc', priority: 1 }];
          } else {
            return []; // Remove sort
          }
        } else {
          return [{ key: columnKey, direction: 'asc', priority: 1 }];
        }
      } else {
        // Multi-column sort
        const existingIndex = prev.findIndex(s => s.key === columnKey);
        if (existingIndex >= 0) {
          const existing = prev[existingIndex];
          if (existing.direction === 'asc') {
            // Change to desc
            const newConfig = [...prev];
            newConfig[existingIndex] = { ...existing, direction: 'desc' };
            return newConfig;
          } else {
            // Remove this sort
            return prev.filter(s => s.key !== columnKey)
              .map((s, index) => ({ ...s, priority: index + 1 }));
          }
        } else {
          // Add new sort
          return [...prev, { key: columnKey, direction: 'asc', priority: prev.length + 1 }];
        }
      }
    });
  };

  // Custom comparison function for specific field types
  const getCustomSortValue = useCallback((value, columnKey) => {
    // First check module-specific custom sort orders
    const customOrder = customSortOrders[columnKey];
    if (customOrder) {
      const index = customOrder.indexOf(value);
      return index >= 0 ? index : customOrder.length;
    }
    
    // Fallback to hardcoded sort orders for backward compatibility
    const fallbackOrder = CUSTOM_SORT_ORDERS[columnKey];
    if (fallbackOrder) {
      const index = fallbackOrder.indexOf(value);
      return index >= 0 ? index : fallbackOrder.length;
    }
    
    return value;
  }, [customSortOrders]);

  const resolveRowSortValue = useCallback(
    (row, sortKey) => {
      const column = columns.find((col) => col.key === sortKey);
      const dataKey = column?.displayField || sortKey;
      let value = row[dataKey];
      if (value === undefined && dataKey !== sortKey) {
        value = row[sortKey];
      }
      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        !(value instanceof Date)
      ) {
        value =
          value.name ??
          value.title ??
          value.label ??
          value.userDisplayName ??
          value.typeName ??
          value.statusName ??
          value.code ??
          value.id;
      }
      return getCustomSortValue(value, sortKey);
    },
    [columns, getCustomSortValue]
  );

  // Sort data based on current sort configuration
  const sortedData = useMemo(() => {
    if (sortConfig.length === 0) return data;

    return [...data].sort((a, b) => {
      for (const sort of sortConfig.sort((a, b) => a.priority - b.priority)) {
        const aVal = resolveRowSortValue(a, sort.key);
        const bVal = resolveRowSortValue(b, sort.key);
        
        let comparison = 0;
        
        // Handle null/undefined values
        if (aVal == null && bVal == null) continue;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        
        // Compare values
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          comparison = aVal.localeCompare(bVal, undefined, { numeric: true });
        } else if (typeof aVal === 'number' && typeof bVal === 'number') {
          comparison = aVal - bVal;
        } else if (aVal instanceof Date && bVal instanceof Date) {
          comparison = aVal.getTime() - bVal.getTime();
        } else {
          comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
        }
        
        if (comparison !== 0) {
          return sort.direction === 'asc' ? comparison : -comparison;
        }
      }
      return 0;
    });
  }, [data, sortConfig, resolveRowSortValue]);

  // Cell content renderer with support for different cell types
  const renderCellContent = useCallback((value, column, row) => {
    // Handle custom render function (highest priority)
    if (column.render && typeof column.render === 'function') {
      return column.render(value, row, column);
    }
    
    // Handle custom cell renderers
    if (column.cellRenderer) {
      if (typeof column.cellRenderer === 'function') {
        return column.cellRenderer(value, row, column, theme, {
          onFileCountClick,
          onTodoTaskCountClick,
          spacing,
          actionsVisibility,
        });
      }
      const renderer = CELL_RENDERERS[column.cellRenderer];
      if (renderer) {
        return renderer(value, column, row, theme, {
          onFileCountClick,
          onTodoTaskCountClick,
          spacing,
          actionsVisibility
        });
      }
    }
    
    // Default rendering
    return renderDefault(value, column, row, theme, {
      spacing,
      actionsVisibility
    });
  }, [theme, onFileCountClick, onTodoTaskCountClick, spacing, actionsVisibility]);

  // Get ordered columns
  const orderedColumns = columnOrder
    .map(key => columns.find(col => col.key === key))
    .filter(Boolean);
    


  const densityConfig = DENSITY_CONFIG[density] || DENSITY_CONFIG.normal;
  const v = tableVariant;

  /** Sticky removes the actions column from normal table flow when tbody is empty — overlap with TITLE. */
  const useStickyActionsColumn = sortedData.length > 0;

  useEffect(() => {
    if (sortedData.length > 0) return;
    const el = scrollContainerRef.current;
    if (el) el.scrollLeft = 0;
  }, [sortedData.length, tableId]);

  const cellLayoutStyle = useCallback(
    (column, { lineClamp = false } = {}) => {
      const width = widthForColumn(column);
      const minWidth =
        column.key === 'actions'
          ? width
          : columnWidths[column.key] || column.width || '120px';

      return {
        width,
        minWidth,
        maxWidth: column.key === 'actions' ? width : 'none',
        whiteSpace: lineClamp ? 'normal' : 'nowrap',
        ...(column.key === 'actions' && useStickyActionsColumn ? { left: 0 } : {}),
      };
    },
    [columnWidths, useStickyActionsColumn, widthForColumn]
  );

  const actionsStickyClass = (inHeader = false) =>
    useStickyActionsColumn
      ? cn('sticky-actions-column', inHeader ? 'z-30' : 'z-20')
      : null;

  const renderActionsHeader = (column) => (
    <span className="sticky-actions-header-label block w-full text-center px-2">
      {column.header}
    </span>
  );

  const isEmptyTable = sortedData.length === 0;

  const tableMinWidthPx = useMemo(() => {
    if (isEmptyTable || orderedColumns.length === 0) return 0;
    return orderedColumns.reduce((sum, column) => {
      return sum + clampColumnWidthPx(widthForColumn(column));
    }, 0);
  }, [isEmptyTable, orderedColumns, widthForColumn]);

  /** Fixed px for actions; last data column absorbs extra table width (w-full + fixed layout). */
  const colWidthStyle = useCallback(
    (column, index, columnCount) => {
      if (column.key === 'actions') {
        const w = widthForColumn(column);
        return { width: w, maxWidth: w };
      }
      if (index === columnCount - 1) {
        return { width: 'auto' };
      }
      return { width: widthForColumn(column) };
    },
    [widthForColumn],
  );

  return (
    <div
      className={cn(v.container(theme), className)}
      data-table-ui={tableUiThemeId}
    >
      {/* Horizontal scroll container with custom scrollbar */}
      <div
        ref={scrollContainerRef}
        className="h-full min-w-0 w-full max-w-full overflow-x-auto overflow-y-auto advanced-table-scroll"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: isDark ? '#6b7280 #374151' : '#d1d5db #f3f4f6',
        }}
      >
        <table
          ref={tableRef}
          className={cn(
            'border-collapse',
            isEmptyTable ? 'w-max min-w-full advanced-table-empty' : 'w-full',
          )}
          style={{
            tableLayout: isEmptyTable ? 'auto' : 'fixed',
            ...(tableMinWidthPx > 0 ? { minWidth: `${tableMinWidthPx}px` } : {}),
          }}
        >
        {!isEmptyTable && (
        <colgroup>
          {orderedColumns.map((column, index) => (
            <col
              key={`col-${column.key}`}
              style={colWidthStyle(column, index, orderedColumns.length)}
            />
          ))}
        </colgroup>
        )}
        <thead className={v.header(theme)}>
          <tr>
            {orderedColumns.map((column) => (
              <th
                key={column.key}
                draggable={column.key !== 'actions'}
                onDragStart={column.key !== 'actions' ? (e) => handleDragStart(e, column.key) : undefined}
                onDragOver={column.key !== 'actions' ? (e) => handleDragOver(e, column.key) : undefined}
                onDrop={column.key !== 'actions' ? (e) => handleDrop(e, column.key) : undefined}
                onDragEnd={column.key !== 'actions' ? handleDragEnd : undefined}
                onDragLeave={column.key !== 'actions' ? () => setDragOverColumn(null) : undefined}
                className={cn(
                  'relative select-none group transition-colors duration-150',
                  'advanced-table-th',
                  `density-${density}`,
                  isRefinedTable
                    ? theme.text.secondary
                    : (v.thExtra?.() || 'text-white'),
                  column.key === 'actions'
                    ? cn(
                        'cursor-default advanced-table-actions-th',
                        actionsStickyClass(true),
                        !isRefinedTable &&
                          useStickyActionsColumn &&
                          cn(theme.gradients.header, 'text-white'),
                        !useStickyActionsColumn &&
                          isRefinedTable &&
                          'bg-[#f8fafc] dark:bg-[#0f172a]'
                      )
                    : 'cursor-move',
                  v.cellBorders(theme),
                  dragOverColumn === column.key && 'bg-blue-50/80 dark:bg-blue-900/20 ring-1 ring-blue-300/60',
                  draggedColumn === column.key && 'opacity-60'
                )}
                style={cellLayoutStyle(column)}
              >
                {column.key === 'actions' ? (
                  renderActionsHeader(column)
                ) : (
                <div className="flex items-center justify-between pl-2">
                  <div 
                    className={cn(
                      'flex items-center gap-2',
                      'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400'
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSort(column.key, e);
                    }}
                    title={`Sort by ${column.header}${sortConfig.length > 0 ? ' (Ctrl+Click for multi-column sort)' : ''}`}
                  >
                    <span className="truncate">{column.header}</span>
                    
                    <div className="advanced-drag-indicator ml-2">
                      <DragHandleIcon className="w-3 h-3" />
                    </div>
                    
                    {(() => {
                      const sortInfo = sortConfig.find(s => s.key === column.key);
                      if (sortInfo) {
                        return (
                          <div className="flex items-center gap-1">
                            <ArrowUpIcon 
                              className={cn(
                                "advanced-sort-indicator active w-4 h-4 transition-all duration-200 drop-shadow-sm",
                                sortInfo.direction === 'desc' && "rotate-180"
                              )} 
                            />
                            {sortConfig.length > 1 && (
                              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-blue-700 dark:text-blue-200 bg-blue-100 dark:bg-blue-900/50 rounded-full border border-blue-200 dark:border-blue-700">
                                {sortInfo.priority}
                              </span>
                            )}
                          </div>
                        );
                      }
                      return (
                        <ArrowUpIcon 
                          className="w-4 h-4 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" 
                        />
                      );
                    })()}
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <DragHandleIcon 
                      className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" 
                    />
                    <div
                      className={cn(
                        "resize-handle absolute right-0 top-0 bottom-0 w-2 cursor-col-resize bg-transparent hover:bg-blue-500 transition-colors z-30 opacity-0 group-hover:opacity-100",
                        isResizing === column.key && "bg-blue-500 opacity-100"
                      )}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleMouseDown(e, column.key);
                      }}
                      title={`Resize ${column.header} column`}
                    />
                  </div>
                </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        
        <tbody>
          {sortedData.map((row, index) => (
            <tr 
              key={index}
              className={cn(
                v.rowBase(theme, densityConfig),
                v.getRowClassName?.(index, theme)
              )}
            >
              {orderedColumns.map((column) => (
                <td
                  key={column.key}
                  className={cn(
                    'relative font-normal',
                    isRefinedTable
                      ? cn('advanced-table-td', theme.text.primary)
                      : cn(theme.text.primary, densityConfig.padding),
                    `density-${density}`,
                    v.cellBorders(theme),
                    column.key === 'actions' && cn(
                      'advanced-table-actions-td',
                      actionsStickyClass(false),
                      !isRefinedTable && theme.colors.surface,
                      !useStickyActionsColumn && isRefinedTable && 'bg-white dark:bg-slate-900'
                    )
                  )}
                  style={cellLayoutStyle(column, {
                    lineClamp: column.cellRenderer === 'lineClamp',
                  })}
                >
                  <div 
                    className={column.key === 'actions' ? cn(
                      'flex justify-center',
                      spacing,
                      getTableActionsVisibilityClass()
                    ) : column.cellRenderer === 'lineClamp' ? 'pl-2' : 'overflow-hidden text-ellipsis pl-2'}
                    // Add tooltip for default truncated text (if no custom renderer)
                    title={
                      !column.cellRenderer && 
                      typeof row[column.key] === 'string' && 
                      row[column.key].length > 50 
                        ? String(row[column.key]) 
                        : undefined
                    }
                  >
                    {renderCellContent(
                      column.displayField ? row[column.displayField] : row[column.key], 
                      column, 
                      row
                    )}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      
      {/* Configuration overlay when resizing */}
      {isResizing && (
        <div className="absolute top-2 right-2 bg-black/80 text-white px-2 py-1 rounded text-xs">
          Resizing: {columnWidths[isResizing]}px
        </div>
      )}
    </div>
  );
};

const TableCustomizationPanel = ({ 
  density, 
  onDensityChange, 
  onResetConfiguration,
  sortConfig = [],
  onClearSort,
  isOpen,
  onClose 
}) => {
  
  if (!isOpen) return null;

  return (
    <div className="absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 min-w-64 z-50">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm">Table Settings</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Sort Status */}
      {sortConfig.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium">Current Sort</label>
            <button
              onClick={onClearSort}
              className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            >
              Clear Sort
            </button>
          </div>
          <div className="space-y-1">
            {sortConfig
              .sort((a, b) => a.priority - b.priority)
              .map((sort, index) => (
                <div key={sort.key} className="flex items-center gap-2 text-sm">
                  <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-blue-600 rounded-full">
                    {sort.priority}
                  </span>
                  <span className="capitalize">{sort.key.replace(/([A-Z])/g, ' $1').trim()}</span>
                  <ArrowUpIcon 
                    className={cn(
                      "w-3 h-3 text-blue-600 dark:text-blue-400 transition-transform",
                      sort.direction === 'desc' && "rotate-180"
                    )} 
                  />
                  <span className="text-xs text-gray-500">
                    {sort.direction === 'asc' ? 'ascending' : 'descending'}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Density Options */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Table Density</label>
        <div className="space-y-2">
          {Object.entries(DENSITY_CONFIG).map(([key, config]) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="density"
                value={key}
                checked={density === key}
                onChange={() => onDensityChange(key)}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">{config.name}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Reset Button */}
      <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
        <button
          onClick={onResetConfiguration}
          className="w-full px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
        >
          Reset Table Configuration
        </button>
      </div>

      {/* Instructions */}
      <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
        <p>• Click column headers to sort</p>
        <p>• Ctrl/Cmd+Click for multi-column sort</p>
        <p>• Drag column headers to reorder</p>
        <p>• Drag right edge to resize columns</p>
        <p>• Settings are saved automatically</p>
      </div>
    </div>
  );
};

export { AdvancedTable, TableCustomizationPanel, DENSITY_CONFIG };
